import { parseWeekString, mondayOfIsoWeek, weeksBetween, weekStringFromDate } from './iso-week'
import type { PauzeVerloning } from './werkbon-pauze'
import {
  type ShiftRoster,
  getShiftForDate,
  dayCodeToPloegSoort,
  isPerWeekNachtRooster,
} from './shift-roster'

export type PloegSoort = 'DAG' | 'VROEG' | 'LAAT' | 'NACHT'

export interface DienstTijden {
  start: string
  eind: string
  /**
   * Optionele per-ploegSoort pauze (minuten). Valt terug op de generator-brede
   * `pauzeMinutes` als die niet is opgegeven — zo blijft een werkschema met
   * afwijkende pauze per ploeg (bv. nacht 45 min) correct, zonder bestaande
   * callers (die één pauze meegeven) te breken.
   */
  pauzeMinutes?: number
  /**
   * FO-473 - verloningsafspraak van deze ploeg (uit het werkschema). Ontbreekt =
   * de cascade bepaalt 'm (klant-default of systeemstandaard).
   */
  pauzeVerloning?: PauzeVerloning
  /**
   * FO-473 - factuurpauze-afspraak van deze ploeg (uit het werkschema).
   * Ontbreekt = de cascade bepaalt 'm (klant-default of afgeleid).
   */
  pauzeFacturatieMinuten?: number
}

export interface RecurringGeneratorInput {
  startWeek: string
  eindWeek: string
  frequentieWeken: number
  dagenPerWeek?: number
  weekdays?: number[]
  ploegRotatie: PloegSoort[]
  dienstTijden: Record<PloegSoort, DienstTijden>
  geplandUrenPerDag: number
  pauzeMinutes: number
  /**
   * Bij nachtdienst: behandel zondag (offset 6) als de zondag VOOR de
   * werkweek, niet de zondag NA de werkweek. Dit is relevant voor
   * ploegendiensten die op zondagnacht starten (23:00→06:00 zondag→maandag).
   *
   * Wanneer true en offset 6 in weekdays:
   * - Zondag entry krijgt date = monday - 1 (vorige dag)
   * - In plaats van monday + 6 (einde van de week)
   */
  zondagnachtAlsWerkweekStart?: boolean
  /**
   * Optioneel actief ploegrooster. Wanneer aanwezig bepaalt de roster (per-dag)
   * of de leidende zondag een nachtdienst is — i.p.v. de per-week ploeg-heuristiek
   * (`ploegRotatie[i]`), die bij een roster niet de werkelijke dag-ploeg is.
   */
  roster?: ShiftRoster
}

export interface RecurringDayEntry {
  date: Date
  plannedStart: string
  plannedEnd: string
  pauzeMinutes: number
}

export interface RecurringIterationOutput {
  weekNr: string
  ploegSoort: PloegSoort
  startDatum: Date
  eindDatum: Date
  dayEntries: RecurringDayEntry[]
}

export function computeRecurringWerkbonCount(
  startWeek: string,
  eindWeek: string,
  frequentieWeken: number,
): number {
  if (frequentieWeken < 1) throw new Error('frequentieWeken moet minstens 1 zijn.')
  const start = parseWeekString(startWeek)
  const end = parseWeekString(eindWeek)
  const diff = weeksBetween(start, end)
  if (diff < 0) return 0
  return Math.floor(diff / frequentieWeken) + 1
}

export function generateRecurringDates(input: RecurringGeneratorInput): RecurringIterationOutput[] {
  if (input.ploegRotatie.length === 0) {
    throw new Error('ploegRotatie moet minimaal 1 ploegsoort bevatten.')
  }
  const weekdays = resolveWeekdays(input)
  // Zondagnacht-preset-fix: bij een PER-WEEK nacht-rooster (zondagen uitsluitend
  // X/N — zie isPerWeekNachtRooster) hoort elke zondag bij de werkweek die hij
  // INLEIDT. Forceer daarom de leidende zondag (offset 6) in de generatie —
  // ongeacht welke create-flow de weekdays meegaf — zodat de inleidende
  // (zondag)nacht in élke werkbon-create verschijnt. De werkelijke X/N-keuze per
  // dag maakt de roster-loop downstream. Volcontinu (dag-werk op zondag, ook bij
  // een 7-voud-cyclus zoals 70 dagen) valt buiten dit mechanisme.
  const perWeekZondagnacht =
    input.zondagnachtAlsWerkweekStart === true &&
    !!input.roster &&
    isPerWeekNachtRooster(input.roster.pattern, input.roster.cycleDays)
  const effectiveWeekdays =
    perWeekZondagnacht && !weekdays.includes(6) ? [...weekdays, 6] : weekdays
  const count = computeRecurringWerkbonCount(input.startWeek, input.eindWeek, input.frequentieWeken)
  const startSpec = parseWeekString(input.startWeek)
  const mondayOfStart = mondayOfIsoWeek(startSpec.week, startSpec.year)
  const results: RecurringIterationOutput[] = []
  for (let i = 0; i < count; i++) {
    const weekOffset = i * input.frequentieWeken
    const monday = new Date(mondayOfStart)
    monday.setUTCDate(mondayOfStart.getUTCDate() + weekOffset * 7)
    const ploegSoort = input.ploegRotatie[i % input.ploegRotatie.length]
    const tijden = input.dienstTijden[ploegSoort]
    const dayEntries: RecurringDayEntry[] = []

    // Bepaalt of de zondag (offset 6) de werkweek INLEIDT (date = monday-1) i.p.v.
    // afsluit (monday+6). Bij een PER-WEEK nacht-rooster (isPerWeekNachtRooster:
    // 7-voud én zondagen uitsluitend X/N) leidt elke zondag de volgende werkweek
    // in — uniform over alle iteraties, zodat elke zondag precies één keer
    // voorkomt (geen duplicaat tussen laat- en nacht-week) en de roster-loop
    // downstream de X/N-keuze maakt. Volcontinu blijft buiten schot (spec §5.4) —
    // óók bij een 7-voud-cyclus met dag-werk op zondag (Bakker-casus). Zonder
    // rooster: per-week ploeg-tijden (23:00→06:00 = eind < start).
    const zondagLeidtWerkweekIn =
      input.zondagnachtAlsWerkweekStart === true &&
      (input.roster
        ? isPerWeekNachtRooster(input.roster.pattern, input.roster.cycleDays)
        : tijden.eind < tijden.start)

    for (const offset of effectiveWeekdays) {
      const date = new Date(monday)

      if (offset === 6 && zondagLeidtWerkweekIn) {
        // Zondagnacht = dag VÓÓR de maandag (zondag van vorige week): monday - 1.
        date.setUTCDate(monday.getUTCDate() - 1)
      } else {
        date.setUTCDate(monday.getUTCDate() + offset)
      }

      dayEntries.push({
        date,
        plannedStart: tijden.start,
        plannedEnd: tijden.eind,
        // Per-ploegSoort pauze (uit het werkschema) wint; anders de generator-brede.
        pauzeMinutes: tijden.pauzeMinutes ?? input.pauzeMinutes,
      })
    }

    // Sorteer entries op datum (zondag kan nu vóór maandag liggen)
    // Lokale array per iteratie — sort-in-place is veilig.
    dayEntries.sort((a, b) => a.date.getTime() - b.date.getTime())

    const startDatum = dayEntries[0].date
    const eindDatum = dayEntries[dayEntries.length - 1].date

    results.push({
      weekNr: weekStringFromDate(monday),
      ploegSoort,
      startDatum,
      eindDatum,
      dayEntries,
    })
  }
  return results
}

/**
 * #48 — losse-dag-pad voor ploegendienst. De gebruiker kiest specifieke dagen;
 * per dag leiden we de juiste ploegsoort (V/L/N) af uit het rooster
 * (`getShiftForDate` → `dayCodeToPloegSoort`), met fallback op `ploegRotatie[0]`
 * als er geen rooster is of de dag een rustdag (X / null) is. De dagen worden
 * gegroepeerd PER PLOEGSOORT tot één werkbon per ploeg (een werkbon heeft één
 * ploegSoort); de dayEntries binnen een werkbon hoeven niet aaneengesloten te zijn.
 */
export interface LosseDagenGeneratorInput {
  /** Gekozen dagen als yyyy-mm-dd. */
  dagen: string[]
  ploegRotatie: PloegSoort[]
  dienstTijden: Record<PloegSoort, DienstTijden>
  pauzeMinutes: number
  roster?: ShiftRoster
}

export function generateLosseDagenDates(
  input: LosseDagenGeneratorInput,
): RecurringIterationOutput[] {
  if (input.ploegRotatie.length === 0) {
    throw new Error('ploegRotatie moet minimaal 1 ploegsoort bevatten.')
  }
  const perPloeg = new Map<PloegSoort, RecurringDayEntry[]>()
  for (const dag of Array.from(new Set(input.dagen)).sort()) {
    const date = new Date(`${dag}T00:00:00.000Z`)
    if (Number.isNaN(date.getTime())) continue
    let ploegSoort: PloegSoort = input.ploegRotatie[0]
    if (input.roster) {
      const mapped = dayCodeToPloegSoort(getShiftForDate(input.roster, date))
      if (mapped) ploegSoort = mapped
    }
    const tijden = input.dienstTijden[ploegSoort]
    const list = perPloeg.get(ploegSoort) ?? []
    list.push({
      date,
      plannedStart: tijden.start,
      plannedEnd: tijden.eind,
      pauzeMinutes: tijden.pauzeMinutes ?? input.pauzeMinutes,
    })
    perPloeg.set(ploegSoort, list)
  }
  const results: RecurringIterationOutput[] = []
  for (const [ploegSoort, dayEntries] of perPloeg) {
    dayEntries.sort((a, b) => a.date.getTime() - b.date.getTime())
    results.push({
      weekNr: weekStringFromDate(dayEntries[0].date),
      ploegSoort,
      startDatum: dayEntries[0].date,
      eindDatum: dayEntries[dayEntries.length - 1].date,
      dayEntries,
    })
  }
  results.sort((a, b) => a.startDatum.getTime() - b.startDatum.getTime())
  return results
}

function resolveWeekdays(input: RecurringGeneratorInput): number[] {
  if (input.weekdays && input.weekdays.length > 0) {
    const unique = Array.from(new Set(input.weekdays)).sort((a, b) => a - b)
    for (const d of unique) {
      if (d < 0 || d > 6) throw new Error('weekdays moeten tussen 0 en 6 liggen.')
    }
    return unique
  }
  if (input.dagenPerWeek == null) {
    throw new Error('Geef weekdays of dagenPerWeek op.')
  }
  if (input.dagenPerWeek < 1 || input.dagenPerWeek > 7) {
    throw new Error('dagenPerWeek moet tussen 1 en 7 liggen.')
  }
  return Array.from({ length: input.dagenPerWeek }, (_, i) => i)
}
